import { useState } from 'react';
import { Link, createFileRoute, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import {
  api,
  errorMessage,
  POI_CATEGORIES,
  POI_CATEGORY_LABELS,
  type AdminRoute,
  type PoiCategory,
} from '@/lib/api';
import { TranslationsField } from '@/components/TranslationsField';
import { LocationPickerMap } from '@/components/LocationPickerMap';
import { RouteCompletionSection } from '@/components/RouteCompletionSection';
import { RoutePlacesSection } from '@/components/RoutePlacesSection';

export const Route = createFileRoute('/_authed/routes/$routeId')({
  component: RouteDetailPage,
});

const tr = z.object({
  en: z.string().min(1),
  el: z.string().optional(),
  es: z.string().optional(),
  de: z.string().optional(),
  it: z.string().optional(),
  ru: z.string().optional(),
  he: z.string().optional(),
  pl: z.string().optional(),
  uk: z.string().optional(),
  fr: z.string().optional(),
  ar: z.string().optional(),
});

const createSchema = z.object({
  shortId: z
    .string()
    .min(1)
    .max(32)
    .regex(/^[a-z0-9-]+$/u, 'lowercase letters / digits / hyphens'),
  position: z.coerce.number().int().min(1),
  category: z.enum(POI_CATEGORIES as [PoiCategory, ...PoiCategory[]]),
  isRequired: z.boolean().default(true),
  title: tr,
  subtitle: tr,
  story: tr,
  lat: z.coerce.number(),
  lng: z.coerce.number(),
});
type CreateValues = z.infer<typeof createSchema>;

function RouteDetailPage() {
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { routeId } = Route.useParams();
  const [creating, setCreating] = useState(false);

  const routeQ = useQuery({
    queryKey: ['route', routeId],
    queryFn: () => api.getRoute(routeId),
  });

  const { data, isLoading } = useQuery({
    queryKey: ['route-locations', routeId],
    queryFn: () => api.listLocations(routeId),
  });

  const create = useMutation({
    mutationFn: (v: CreateValues) =>
      api.createLocation({
        routeId,
        shortId: v.shortId,
        position: v.position,
        category: v.category,
        isRequired: v.isRequired,
        title: v.title,
        subtitle: v.subtitle,
        story: v.story,
        lat: v.lat,
        lng: v.lng,
      }),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['route-locations', routeId] });
      setCreating(false);
      // Jump straight into the new location so the editor can drop in
      // images / quiz / badge without a list round-trip.
      navigate({
        to: '/locations/$locationId',
        params: { locationId: data.location.id },
      });
    },
  });

  const del = useMutation({
    mutationFn: () => api.deleteRoute(routeId),
    onSuccess: () => {
      const villageId = routeQ.data?.route.villageId;
      if (villageId) {
        queryClient.invalidateQueries({ queryKey: ['routes', villageId] });
        navigate({ to: '/villages/$villageId', params: { villageId } });
      } else {
        navigate({ to: '/villages' });
      }
    },
  });

  // Deleting a route cascades to every POI in it (schema: locations.route_id
  // ON DELETE CASCADE), so the confirm spells out exactly what goes with it.
  const onDelete = () => {
    if (del.isPending) return;
    const n = data?.locations.length ?? 0;
    const name = routeQ.data?.route.name.en?.trim() || 'this route';
    const msg =
      n > 0
        ? `Delete route "${name}"?\n\nThis permanently deletes the route AND its ${n} POI${n === 1 ? '' : 's'} (with their images, quiz, badge and perk). This cannot be undone.`
        : `Delete route "${name}"? This cannot be undone.`;
    if (window.confirm(msg)) del.mutate();
  };

  const nextPosition = data?.locations.length
    ? Math.max(...data.locations.map((l) => l.position)) + 1
    : 1;

  if (isLoading) return <div className="card text-slate-gray">Loading locations…</div>;

  return (
    <div className="space-y-6">
      <div>
        <div className="flex items-center justify-between gap-2">
          <Link
            to={routeQ.data ? '/villages/$villageId' : '/villages'}
            params={
              routeQ.data ? { villageId: routeQ.data.route.villageId } : undefined
            }
            className="btn-ghost -ml-3 mb-2"
          >
            ← Back
          </Link>
          <button
            type="button"
            className="btn-ghost text-error hover:bg-error/10"
            onClick={onDelete}
            disabled={del.isPending}
          >
            {del.isPending ? 'Deleting…' : 'Delete route'}
          </button>
        </div>
        {del.isError ? (
          <p className="mb-2 text-caption text-error">{errorMessage(del.error)}</p>
        ) : null}
        {routeQ.data ? (
          <RouteHeader route={routeQ.data.route} />
        ) : (
          <h1 className="text-h1">Loading route…</h1>
        )}
        <div className="mt-4 flex items-center justify-between">
          <div>
            <h2 className="text-h2">Locations</h2>
            <p className="mt-1 text-body text-slate-gray">
              Each location shows up as a QR stop on the route.
            </p>
          </div>
          {!creating ? (
            <button className="btn-primary" onClick={() => setCreating(true)}>
              + New location
            </button>
          ) : null}
        </div>
      </div>

      {creating ? (
        <CreateLocationForm
          defaultPosition={nextPosition}
          onSubmit={(v) => create.mutateAsync(v)}
          submitting={create.isPending}
          error={create.error ? errorMessage(create.error) : null}
          onCancel={() => setCreating(false)}
        />
      ) : null}

      <div className="grid gap-3">
        {data?.locations.map((l) => (
          <Link
            key={l.id}
            to="/locations/$locationId"
            params={{ locationId: l.id }}
            className="card flex items-center justify-between hover:border-tech-blue/40"
          >
            <div className="flex items-center gap-4">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-tech-blue/10 font-semibold text-tech-blue">
                {l.position}
              </span>
              <div>
                <p className="font-semibold text-dark-navy">{l.title.en}</p>
                <p className="text-caption text-slate-gray">
                  {l.shortId} · {l.subtitle.en}
                </p>
              </div>
            </div>
            <span className="text-caption text-slate-gray">→</span>
          </Link>
        ))}
        {data && data.locations.length === 0 && !creating ? (
          <div className="card text-slate-gray">
            No locations yet. Click <strong>+ New location</strong> to add one.
          </div>
        ) : null}
      </div>

      {/* End-of-route content — shown to the visitor after the final POI. */}
      <RoutePlacesSection routeId={routeId} />
      {routeQ.data ? <RouteCompletionSection route={routeQ.data.route} /> : null}
    </div>
  );
}

// Inline name editor for the route. Two states — read-only header chip
// (default) and an expanded TranslationsField form. We keep the API mutation
// scoped to the form so the rest of the page doesn't re-render when the user
// is just typing in the name input.
function RouteHeader({ route }: { route: AdminRoute }) {
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);

  const save = useMutation({
    mutationFn: (name: AdminRoute['name']) =>
      api.updateRoute(route.id, { name }),
    onSuccess: () => {
      // Refresh both the per-route detail (this page) and the village's
      // route list so the renamed route shows up correctly there too.
      queryClient.invalidateQueries({ queryKey: ['route', route.id] });
      queryClient.invalidateQueries({ queryKey: ['routes', route.villageId] });
      setEditing(false);
    },
  });

  if (editing) {
    return (
      <RouteNameForm
        defaults={route.name}
        submitting={save.isPending}
        error={save.error ? errorMessage(save.error) : null}
        onSubmit={(name) => save.mutateAsync(name)}
        onCancel={() => setEditing(false)}
      />
    );
  }

  return (
    <div className="flex flex-wrap items-center gap-3">
      <h1 className="text-h1">{route.name.en}</h1>
      <button className="btn-ghost" onClick={() => setEditing(true)}>
        Edit name
      </button>
    </div>
  );
}

const routeNameSchema = z.object({ name: tr });
type RouteNameValues = z.infer<typeof routeNameSchema>;

function RouteNameForm({
  defaults,
  submitting,
  error,
  onSubmit,
  onCancel,
}: {
  defaults: AdminRoute['name'];
  submitting: boolean;
  error: string | null;
  onSubmit: (name: AdminRoute['name']) => Promise<unknown>;
  onCancel: () => void;
}) {
  const { register, handleSubmit, setValue, watch } = useForm<RouteNameValues>({
    resolver: zodResolver(routeNameSchema),
    defaultValues: { name: defaults },
  });
  return (
    <form
      onSubmit={handleSubmit((v) => onSubmit(v.name))}
      className="card space-y-3"
    >
      <TranslationsField
        label="Route name"
        required
        basePath="name"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}
      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Saving…' : 'Save name'}
        </button>
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

function CreateLocationForm({
  defaultPosition,
  onSubmit,
  submitting,
  error,
  onCancel,
}: {
  defaultPosition: number;
  onSubmit: (v: CreateValues) => Promise<unknown>;
  submitting: boolean;
  error: string | null;
  onCancel: () => void;
}) {
  const {
    register,
    handleSubmit,
    watch,
    setValue,
    formState: { errors },
  } = useForm<CreateValues>({
    resolver: zodResolver(createSchema),
    defaultValues: {
      shortId: '',
      position: defaultPosition,
      category: 'other',
      isRequired: true,
      title: { en: '' },
      subtitle: { en: '' },
      story: { en: '' },
      lat: 0,
      lng: 0,
    },
  });
  const lat = watch('lat');
  const lng = watch('lng');

  return (
    <form
      onSubmit={handleSubmit((v) => onSubmit(v))}
      className="card space-y-4"
    >
      <h2 className="text-h2">New location</h2>
      <p className="text-caption text-slate-gray">
        After creating, you'll land on the location editor — that's where
        you upload images, add quiz questions, define the badge, and link
        the perk. Get the basics in here.
      </p>

      <div className="grid gap-4 sm:grid-cols-3">
        <div>
          <label className="label">Short ID</label>
          <input
            className="input font-mono"
            placeholder="omd-mon"
            {...register('shortId')}
          />
          {errors.shortId ? (
            <p className="mt-1 text-caption text-error">{errors.shortId.message}</p>
          ) : null}
        </div>
        <div>
          <label className="label">Position</label>
          <input
            className="input"
            type="number"
            min={1}
            {...register('position')}
          />
        </div>
        <div>
          <label className="label">Category</label>
          <select className="input" {...register('category')}>
            {POI_CATEGORIES.map((c) => (
              <option key={c} value={c}>
                {POI_CATEGORY_LABELS[c]}
              </option>
            ))}
          </select>
        </div>
      </div>

      <label className="inline-flex items-center gap-2 text-body">
        <input type="checkbox" {...register('isRequired')} />
        Required for route completion
      </label>

      <TranslationsField
        label="Title"
        required
        basePath="title"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      <TranslationsField
        label="Subtitle"
        required
        basePath="subtitle"
        register={register}
        setValue={setValue}
        watch={watch}
      />
      <TranslationsField
        label="Story"
        required
        multiline
        basePath="story"
        register={register}
        setValue={setValue}
        watch={watch}
      />

      <LocationPickerMap
        label="POI location on the map"
        value={{ lat: Number(lat) || 0, lng: Number(lng) || 0 }}
        onChange={(next) => {
          setValue('lat', next.lat, { shouldDirty: true });
          setValue('lng', next.lng, { shouldDirty: true });
        }}
      />

      {error ? (
        <div className="rounded-lg border border-error/30 bg-error/10 p-3 text-caption text-error">
          {error}
        </div>
      ) : null}

      <div className="flex items-center gap-3">
        <button type="submit" className="btn-primary" disabled={submitting}>
          {submitting ? 'Creating…' : 'Create location'}
        </button>
        <button type="button" className="btn-ghost" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}
